
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';
import type { SettingsTabProps } from './types';

const AppearanceSettingsTab: React.FC<SettingsTabProps> = ({ settings, onSettingsChange }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsToUpdate: Record<string, any>) => {
      console.log('Updating appearance settings:', settingsToUpdate);
      
      const updatePromises = Object.entries(settingsToUpdate).map(async ([key, value]) => {
        const jsonValue: Json = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null
          ? value
          : JSON.stringify(value);
          
        const { error } = await supabase
          .from('website_settings')
          .upsert({ 
            key, 
            value: jsonValue, 
            description: `Setting for ${key}` 
          }, {
            onConflict: 'key'
          });
          
        if (error) {
          throw error;
        }
      });
      
      await Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-settings'] });
      toast.success('Appearance settings updated successfully');
      setErrors({});
    },
    onError: (error: any) => {
      console.error('Failed to update appearance settings:', error);
      toast.error('Failed to update appearance settings');
      setErrors({ appearance: error.message || 'Failed to save settings' });
    },
  });

  const validateSettings = () => {
    const newErrors: Record<string, string> = {};
    
    if (settings.logo_url && !settings.logo_url.match(/^https?:\/\/.+\.(jpg|jpeg|png|gif|svg|webp)$/i)) {
      newErrors.logo_url = 'Logo URL must be a valid image URL';
    }
    
    if (settings.favicon_url && !settings.favicon_url.match(/^https?:\/\/.+\.(ico|png|gif|svg)$/i)) {
      newErrors.favicon_url = 'Favicon URL must be a valid icon file URL';
    }

    // Validate logo sizes - ensure proper number conversion
    const logoMobileHeight = Number(settings.logo_mobile_height) || 48;
    const logoDesktopHeight = Number(settings.logo_desktop_height) || 64;
    const logoMaxWidth = Number(settings.logo_max_width) || 200;
    const logoMaxWidthDesktop = Number(settings.logo_max_width_desktop) || 280;

    if (logoMobileHeight < 24 || logoMobileHeight > 100) {
      newErrors.logo_mobile_height = 'Mobile logo height must be between 24px and 100px';
    }
    
    if (logoDesktopHeight < 32 || logoDesktopHeight > 150) {
      newErrors.logo_desktop_height = 'Desktop logo height must be between 32px and 150px';
    }

    if (logoMaxWidth < 100 || logoMaxWidth > 400) {
      newErrors.logo_max_width = 'Mobile logo max width must be between 100px and 400px';
    }

    if (logoMaxWidthDesktop < 150 || logoMaxWidthDesktop > 600) {
      newErrors.logo_max_width_desktop = 'Desktop logo max width must be between 150px and 600px';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateSettings()) {
      toast.error('Please fix the validation errors before saving');
      return;
    }

    const appearanceSettings = {
      logo_url: settings.logo_url,
      favicon_url: settings.favicon_url,
      logo_mobile_height: Number(settings.logo_mobile_height) || 48,
      logo_desktop_height: Number(settings.logo_desktop_height) || 64,
      logo_max_width: Number(settings.logo_max_width) || 200,
      logo_max_width_desktop: Number(settings.logo_max_width_desktop) || 280,
      footer_text: settings.footer_text,
      discord_url: settings.discord_url,
      twitter_url: settings.twitter_url,
      youtube_url: settings.youtube_url,
      github_url: settings.github_url,
      donate_url: settings.donate_url,
      coffee_url: settings.coffee_url,
      donate_text: settings.donate_text,
      coffee_text: settings.coffee_text,
    };

    console.log('Saving appearance settings:', appearanceSettings);
    updateSettingsMutation.mutate(appearanceSettings);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance Settings</CardTitle>
        <CardDescription>Customize the look and feel of your website</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {errors.appearance && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {errors.appearance}
          </div>
        )}

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Logo Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="logo_url">Logo URL</Label>
              <Input
                id="logo_url"
                value={settings.logo_url || ''}
                onChange={(e) => onSettingsChange('logo_url', e.target.value)}
                placeholder="https://example.com/logo.png"
                className={errors.logo_url ? 'border-red-500' : ''}
              />
              {errors.logo_url && (
                <p className="text-sm text-red-600">{errors.logo_url}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="favicon_url">Favicon URL</Label>
              <Input
                id="favicon_url"
                value={settings.favicon_url || ''}
                onChange={(e) => onSettingsChange('favicon_url', e.target.value)}
                placeholder="https://example.com/favicon.ico"
                className={errors.favicon_url ? 'border-red-500' : ''}
              />
              {errors.favicon_url && (
                <p className="text-sm text-red-600">{errors.favicon_url}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="logo_mobile_height">Mobile Logo Height (px)</Label>
              <Input
                id="logo_mobile_height"
                type="number"
                min="24"
                max="100"
                value={String(settings.logo_mobile_height || 48)}
                onChange={(e) => onSettingsChange('logo_mobile_height', parseInt(e.target.value) || 48)}
                placeholder="48"
                className={errors.logo_mobile_height ? 'border-red-500' : ''}
              />
              {errors.logo_mobile_height && (
                <p className="text-sm text-red-600">{errors.logo_mobile_height}</p>
              )}
              <p className="text-xs text-gray-500">Height of logo on mobile devices (24-100px)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_desktop_height">Desktop Logo Height (px)</Label>
              <Input
                id="logo_desktop_height"
                type="number"
                min="32"
                max="150"
                value={String(settings.logo_desktop_height || 64)}
                onChange={(e) => onSettingsChange('logo_desktop_height', parseInt(e.target.value) || 64)}
                placeholder="64"
                className={errors.logo_desktop_height ? 'border-red-500' : ''}
              />
              {errors.logo_desktop_height && (
                <p className="text-sm text-red-600">{errors.logo_desktop_height}</p>
              )}
              <p className="text-xs text-gray-500">Height of logo on desktop (32-150px)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="logo_max_width">Mobile Logo Max Width (px)</Label>
              <Input
                id="logo_max_width"
                type="number"
                min="100"
                max="400"
                value={String(settings.logo_max_width || 200)}
                onChange={(e) => onSettingsChange('logo_max_width', parseInt(e.target.value) || 200)}
                placeholder="200"
                className={errors.logo_max_width ? 'border-red-500' : ''}
              />
              {errors.logo_max_width && (
                <p className="text-sm text-red-600">{errors.logo_max_width}</p>
              )}
              <p className="text-xs text-gray-500">Maximum width on mobile (100-400px)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_max_width_desktop">Desktop Logo Max Width (px)</Label>
              <Input
                id="logo_max_width_desktop"
                type="number"
                min="150"
                max="600"
                value={String(settings.logo_max_width_desktop || 280)}
                onChange={(e) => onSettingsChange('logo_max_width_desktop', parseInt(e.target.value) || 280)}
                placeholder="280"
                className={errors.logo_max_width_desktop ? 'border-red-500' : ''}
              />
              {errors.logo_max_width_desktop && (
                <p className="text-sm text-red-600">{errors.logo_max_width_desktop}</p>
              )}
              <p className="text-xs text-gray-500">Maximum width on desktop (150-600px)</p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="footer_text">Footer Text</Label>
          <Textarea
            id="footer_text"
            value={settings.footer_text || ''}
            onChange={(e) => onSettingsChange('footer_text', e.target.value)}
            placeholder="Copyright text or other footer information"
            rows={3}
          />
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Social Media Links</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="discord_url">Discord URL</Label>
              <Input
                id="discord_url"
                value={settings.discord_url || ''}
                onChange={(e) => onSettingsChange('discord_url', e.target.value)}
                placeholder="https://discord.gg/..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="twitter_url">Twitter URL</Label>
              <Input
                id="twitter_url"
                value={settings.twitter_url || ''}
                onChange={(e) => onSettingsChange('twitter_url', e.target.value)}
                placeholder="https://twitter.com/..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="youtube_url">YouTube URL</Label>
              <Input
                id="youtube_url"
                value={settings.youtube_url || ''}
                onChange={(e) => onSettingsChange('youtube_url', e.target.value)}
                placeholder="https://youtube.com/..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="github_url">GitHub URL</Label>
              <Input
                id="github_url"
                value={settings.github_url || ''}
                onChange={(e) => onSettingsChange('github_url', e.target.value)}
                placeholder="https://github.com/..."
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Support Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="donate_url">Donate URL (PayPal)</Label>
              <Input
                id="donate_url"
                value={settings.donate_url || ''}
                onChange={(e) => onSettingsChange('donate_url', e.target.value)}
                placeholder="https://paypal.me/..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="donate_text">Donate Button Text</Label>
              <Input
                id="donate_text"
                value={settings.donate_text || ''}
                onChange={(e) => onSettingsChange('donate_text', e.target.value)}
                placeholder="Support Us"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="coffee_url">Buy Me Coffee URL</Label>
              <Input
                id="coffee_url"
                value={settings.coffee_url || ''}
                onChange={(e) => onSettingsChange('coffee_url', e.target.value)}
                placeholder="https://buymeacoffee.com/..."
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="coffee_text">Coffee Button Text</Label>
              <Input
                id="coffee_text"
                value={settings.coffee_text || ''}
                onChange={(e) => onSettingsChange('coffee_text', e.target.value)}
                placeholder="Buy Me a Coffee"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleSave} 
            disabled={updateSettingsMutation.isPending}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {updateSettingsMutation.isPending ? 'Saving...' : 'Save Appearance Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppearanceSettingsTab;
